import type { DegapCycle } from '@/types/prioritization/degapCycle';
import type { Track } from '@/types/prioritization/track';
import type { CatchballLevelConfig } from '@/types/prioritization/catchballLoop';
import type { CycleDesignerType, ResponseType, TrackParams } from '../types';
import {
  CATCHBALL_LEVEL_CONFIG_METADATA_KEY,
  DESIGNER_COLOR_METADATA_KEY,
  DESIGNER_PATH_ORG_UNITS_KEY,
  DESIGNER_TRACK_PARAMS_KEY,
} from './launchCycleDesigner';
import { getDesignerLaunchedAt } from './cycleDesignerPhase';

/** Constraints the cycle designer stamps for downstream Track setup. */
export interface CycleDesignerConstraints {
  source: 'cycle_designer';
  cycleDesignerType: CycleDesignerType;
  frameCount: number;
  orgScopeNodeId?: string;
  catchballPathConfigured: boolean;
  /** Expedited cycles de-emphasize open exploration in track setup. */
  simplifiedMode: boolean;
  strategicIntent?: string;
  maxIterations?: number;
}

/** Constraints inherited by session setup from cycle + track metadata. */
export interface FrameSessionConstraints {
  cycleDesignerType?: CycleDesignerType;
  responseType?: ResponseType;
  scopeOrgUnitId?: string;
  catchballLevelConfig?: CatchballLevelConfig;
  designerColor?: string;
  /** Participation largely fixed when catchball path defines org scope. */
  participationPreset: boolean;
}

export function parseCycleDesignerConstraints(
  cycle: DegapCycle | null | undefined,
): CycleDesignerConstraints | null {
  if (!cycle?.metadata || typeof cycle.metadata !== 'object') return null;
  if (!getDesignerLaunchedAt(cycle)) return null;

  const meta = cycle.metadata as Record<string, unknown>;
  const typeRaw = meta.cycleDesignerType;
  const cycleDesignerType: CycleDesignerType =
    typeRaw === 'expedited' || typeRaw === 'discovery' ? typeRaw : 'standard';

  const frameCount = typeof meta.designerFrameCount === 'number' ? meta.designerFrameCount : 0;

  return {
    source: 'cycle_designer',
    cycleDesignerType,
    frameCount,
    orgScopeNodeId: cycle.orgScopeNodeId,
    catchballPathConfigured: frameCount > 0,
    simplifiedMode: cycleDesignerType === 'expedited',
    strategicIntent: typeof meta.strategicIntent === 'string' ? meta.strategicIntent : undefined,
    maxIterations: typeof meta.maxIterations === 'number' ? meta.maxIterations : undefined,
  };
}

export function parseFrameSessionConstraints(
  track: Track,
  cycle: DegapCycle | null | undefined,
): FrameSessionConstraints {
  const meta = (track.metadata ?? {}) as Record<string, unknown>;
  const cycleConstraints = parseCycleDesignerConstraints(cycle);
  const trackParams = meta[DESIGNER_TRACK_PARAMS_KEY] as TrackParams | undefined;
  const levelConfig = meta[CATCHBALL_LEVEL_CONFIG_METADATA_KEY] as CatchballLevelConfig | undefined;
  const pathIds = meta[DESIGNER_PATH_ORG_UNITS_KEY];

  return {
    cycleDesignerType: cycleConstraints?.cycleDesignerType,
    responseType: trackParams?.responseType,
    scopeOrgUnitId: track.scopeEntityId ?? undefined,
    catchballLevelConfig: levelConfig,
    designerColor:
      typeof meta[DESIGNER_COLOR_METADATA_KEY] === 'string'
        ? (meta[DESIGNER_COLOR_METADATA_KEY] as string)
        : undefined,
    participationPreset: Array.isArray(pathIds) && pathIds.length > 1,
  };
}

/** Which design tabs to surface when cycle constraints apply (hide the rest). */
export function visibleFrameDesignTabs(
  constraints: CycleDesignerConstraints | null,
): Array<'overview' | 'participation' | 'pool' | 'criteria' | 'values'> {
  if (!constraints) {
    return ['overview', 'participation', 'pool', 'criteria', 'values'];
  }
  if (constraints.simplifiedMode) {
    return ['overview', 'pool', 'criteria'];
  }
  // A6 (owner, 2026-07-08): the catchball path presets participation but must not
  // HIDE it — people still need to see and adjust who participates in the track.
  return ['overview', 'participation', 'pool', 'criteria', 'values'];
}
